import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const defaultErrors = {
  maxFileSize: false,
  maxNumber: false,
  acceptType: false,
  resolution: false,
};

const getBase64 = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => resolve(String(reader.result)));
  reader.readAsDataURL(file);
});

/**
 * Reemplazo local de react-images-uploading sin defaultProps (compatible React 18).
 * Mantiene la misma API de render props usada por UploadImage.
 */
export default function ImageUploading({
  multiple = false,
  onChange,
  maxNumber = 1000,
  acceptType = [],
  maxFileSize,
  resolutionWidth,
  resolutionHeight,
  resolutionType,
  onError,
  defaultValue,
  children,
}) {
  const inputRef = useRef(null);
  const [imageList, setImageList] = useState(() => (
    defaultValue?.length
      ? defaultValue.map((item) => ({
        ...item,
        key: item.dataURL,
      }))
      : []
  ));
  const [keyUpdate, setKeyUpdate] = useState('');
  const [errors, setErrors] = useState({ ...defaultErrors });

  const onStandardizeDataChange = (list) => {
    if (!onChange) {
      return;
    }
    const standardized = list.map(({ key, onUpdate, onRemove, ...rest }) => rest);
    onChange(standardized);
  };

  const handleClickInput = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onImageRemove = (key) => {
    setImageList((previousList) => {
      const updatedList = previousList.filter((item) => item.key !== key);
      onStandardizeDataChange(updatedList);
      return updatedList;
    });
  };

  const onImageUpdate = (key) => {
    setKeyUpdate(key);
    handleClickInput();
  };

  const onImageUpload = useCallback(() => {
    setKeyUpdate((prevKey) => (prevKey ? '' : prevKey));
    handleClickInput();
  }, [handleClickInput]);

  const onImageRemoveAll = useCallback(() => {
    setImageList([]);
    onStandardizeDataChange([]);
  }, []);

  const getListFile = async (files) => {
    const fileListBase64 = await Promise.all(Array.from(files).map(getBase64));
    return fileListBase64.map((base64, index) => {
      const key = `${new Date().getTime()}-${files[index].name}`;
      return {
        dataURL: base64,
        file: files[index],
        key,
        onUpdate: () => onImageUpdate(key),
        onRemove: () => onImageRemove(key),
      };
    });
  };

  const validate = async (fileList) => {
    const newErrors = { ...defaultErrors };

    if (maxNumber && !keyUpdate && fileList.length + imageList.length > maxNumber) {
      newErrors.maxNumber = true;
    } else {
      for (let i = 0; i < fileList.length; i += 1) {
        const { file, dataURL } = fileList[i];
        if (file) {
          if (!file.type.includes('image')) {
            newErrors.acceptType = true;
            break;
          }
          if (maxFileSize && file.size > maxFileSize) {
            newErrors.maxFileSize = true;
            break;
          }
          if (acceptType?.length > 0) {
            const type = file.name.split('.').pop() || '';
            if (acceptType.indexOf(type) < 0) {
              newErrors.acceptType = true;
              break;
            }
          }
        }
        if (dataURL && resolutionType) {
          const checkRes = await checkResolution(
            dataURL,
            resolutionType,
            resolutionWidth,
            resolutionHeight,
          );
          if (!checkRes) {
            newErrors.resolution = true;
            break;
          }
        }
      }
    }

    setErrors(newErrors);
    if (Object.values(newErrors).find(Boolean)) {
      onError?.(newErrors, fileList);
      return false;
    }
    return true;
  };

  const onInputChange = async (event) => {
    const { files } = event.target;
    if (!files?.length) {
      return;
    }

    const fileList = await getListFile(files);
    if (!fileList.length) {
      return;
    }

    const isValid = await validate(fileList);
    if (!isValid) {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    let updatedFileList;
    if (keyUpdate) {
      updatedFileList = imageList.map((item) => (
        item.key === keyUpdate ? { ...fileList[0] } : item
      ));
    } else if (multiple) {
      updatedFileList = [...imageList, ...fileList];
      if (maxNumber && updatedFileList.length > maxNumber) {
        updatedFileList = imageList;
      }
    } else {
      updatedFileList = [fileList[0]];
    }

    setImageList(updatedFileList);
    onStandardizeDataChange(updatedFileList);
    setKeyUpdate('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const acceptString = acceptType?.length
    ? acceptType.map((item) => `.${item}`).join(', ')
    : 'image/*';

  return (
    <>
      <input
        type="file"
        accept={acceptString}
        ref={inputRef}
        multiple={multiple && !keyUpdate}
        onChange={onInputChange}
        style={{ display: 'none' }}
      />
      {children?.({
        imageList,
        onImageUpload,
        onImageRemoveAll,
        errors,
      })}
    </>
  );
}

async function checkResolution(
  dataURL,
  resolutionType = 'absolute',
  resolutionWidth = 0,
  resolutionHeight = 1,
) {
  const image = new Image();
  return new Promise((resolve) => {
    image.addEventListener('load', () => {
      if (!image.width || !image.height) {
        resolve(true);
        return;
      }
      switch (resolutionType) {
        case 'absolute':
          resolve(image.width === resolutionWidth && image.height === resolutionHeight);
          break;
        case 'ratio': {
          const ratio = resolutionWidth / resolutionHeight;
          resolve(image.width / image.height === ratio);
          break;
        }
        case 'less':
          resolve(image.width <= resolutionWidth && image.height <= resolutionHeight);
          break;
        case 'more':
          resolve(image.width >= resolutionWidth && image.height >= resolutionHeight);
          break;
        default:
          resolve(true);
      }
    });
    image.src = dataURL;
  });
}

ImageUploading.propTypes = {
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  maxNumber: PropTypes.number,
  acceptType: PropTypes.arrayOf(PropTypes.string),
  maxFileSize: PropTypes.number,
  resolutionWidth: PropTypes.number,
  resolutionHeight: PropTypes.number,
  resolutionType: PropTypes.string,
  onError: PropTypes.func,
  defaultValue: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.func.isRequired,
};
